import React, { useState, useEffect } from 'react';
import { Card, Space, Tag, Typography, Badge, Button, Divider, Avatar, Tooltip } from 'antd';
import {
    ClockCircleOutlined,
    AppstoreOutlined,
    EyeOutlined,
    HeartOutlined,
    HeartFilled,
    CalendarOutlined,
    ShopOutlined,
    EnvironmentOutlined,
    FireOutlined,
    TrophyOutlined,
    UserOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import favoriteService from '../../services/favoriteService';
import { message } from 'antd';
import { toggleFavoriteAuction } from '../../redux/userSlice';
import env from '../../env';
import CompanyDrawer from '../Common/CompanyDrawer';
import { startServerTimeSync, getServerNow } from '../../utils/serverTime';
import './AuctionDetail.css';

const { Title, Text } = Typography;

const AuctionCard = ({
    auction,
    viewMode = 'grid',
    onFavoriteToggle,
    isLiveAuction = false,
    customButtons = null
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { currentUser } = useSelector((state) => state.user);
    const [companyDrawerVisible, setCompanyDrawerVisible] = useState(false);
    // Forces a re-render every 30s so the "Starts In"/"Enter Auction" state
    // (derived from the synced server clock below) doesn't go stale between
    // parent re-renders - same clock every card on the page shares.
    const [, setClockTick] = useState(0);

    useEffect(() => {
        startServerTimeSync();
        const interval = setInterval(() => setClockTick(tick => tick + 1), 30000);
        return () => clearInterval(interval);
    }, []);

    const getTimeLeft = (auction) => {
        const now = moment(getServerNow());
        const start = moment(auction.start_time);
        const timeUntilStart = moment.duration(start.diff(now));
        
        // If auction hasn't started yet, show countdown
        if (timeUntilStart.asMilliseconds() > 0) {
            if (timeUntilStart.asDays() > 1) {
                return `${Math.floor(timeUntilStart.asDays())} days left`;
            } else if (timeUntilStart.asHours() > 1) {
                return `${Math.floor(timeUntilStart.asHours())} hours left`;
            } else {
                return `${Math.floor(timeUntilStart.asMinutes())} minutes left`;
            }
        }
        
        // Auction has started - show status based on auction.status
        if (auction.status === 'completed') {
            return 'Completed';
        } else if (auction.status === 'active') {
            return 'LIVE NOW';
        } else {
            return 'Started';
        }
    };

    const getAuctionStatus = (auction) => {
        const now = moment(getServerNow());
        const start = moment(auction.start_time);
        
        // If auction hasn't started yet
        if (now.isBefore(start)) {
            return 'Scheduled';
        }
        
        // Auction has started - show status based on auction.status
        if (auction.status === 'completed') {
            return 'Completed';
        } else if (auction.status === 'active') {
            return 'Started';
        } else {
            return 'Started';
        }
    };

    const formatDateTime = (dateTime) => {
        if (!dateTime) return 'TBD';
        
        // Handle multiple datetime formats
        let parsedDate;
        
        if (typeof dateTime === 'string') {
            // Try parsing as ISO string first
            parsedDate = moment(dateTime);
            
            // If invalid, try other formats
            if (!parsedDate.isValid()) {
                // Try MySQL datetime format
                parsedDate = moment(dateTime, 'YYYY-MM-DD HH:mm:ss');
            }
            
            if (!parsedDate.isValid()) {
                // Try date only
                parsedDate = moment(dateTime, 'YYYY-MM-DD');
            }
        } else {
            parsedDate = moment(dateTime);
        }
        
        return parsedDate.isValid() ? parsedDate.format('MMM D, YYYY h:mm A') : 'Invalid Date';
    };

    const getStatusColor = (status) => {
        const statusLower = status.toLowerCase();
        const colors = {
            draft: 'default',
            scheduled: 'processing',
            active: 'success',
            completed: 'success',
            cancelled: 'error'
        };
        return colors[statusLower] || 'default';
    };

    const toggleFavorite = async (auctionId, e) => {
        e.stopPropagation();
        if (!currentUser) {
            message.warning('Please login to add favorites');
            return;
        }

        // Call the parent callback if provided
        if (onFavoriteToggle) {
            onFavoriteToggle(auctionId);
        } else {
            // Default behavior - update Redux state
            dispatch(toggleFavoriteAuction(auctionId));
        }

        try {
            const response = await favoriteService.toggleFavoriteAuction(auctionId);
            console.log('Favorite toggle response:', response);
        } catch (error) {
            console.error('Error toggling favorite:', error);
            console.error('Error details:', error.response?.data);
            message.error(error.response?.data?.message || 'Failed to update favorite status');
        }
    };

    // Show "Enter Auction" whenever there's still something to enter - both
    // while live/active AND while scheduled, so users can go wait in the
    // live-auction room before it starts. Only "View Auction" once completed
    // or cancelled. The green pulsing style is reserved for genuinely live.
    const canEnterAuction = auction.status !== 'completed' && auction.status !== 'cancelled';
    const isAuctionLiveNow = isLiveAuction || auction.status === 'live' || auction.status === 'active';

    return (
        <div>
        <Card
            hoverable
            onClick={() => {
                // Always navigate to auction detail page when clicking the card
                navigate(`/auctions/${auction.slug}`);
            }}
            style={{
                borderRadius: '16px',
                overflow: 'hidden',
                height: viewMode === 'list' ? 'auto' : '100%',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                border: '1px solid #e5e7eb',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                background: '#ffffff',
                position: 'relative'
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12)';
                e.currentTarget.style.borderColor = '#d1d5db';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.06)';
                e.currentTarget.style.borderColor = '#e5e7eb';
            }}
            cover={
                <div style={{ position: 'relative' }}>
                    <div style={{ 
                        position: 'relative',
                        height: viewMode === 'list' ? 280 : 200,
                        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                        overflow: 'hidden'
                    }}>
                        {auction.image ? (
                            <img
                                alt={auction.title}
                                src={env.BACKEND_STORAGE_URL + '/' + auction.image}
                                style={{ 
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                                }}
                                onMouseOver={(e) => e.target.style.transform = 'scale(1.08)'}
                                onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                }}
                            />
                        ) : null}
                        <div
                            style={{
                                width: '100%',
                                height: '100%',
                                display: auction.image ? 'none' : 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                flexDirection: 'column',
                                gap: '16px'
                            }}
                        >
                            <div style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '50%',
                                background: 'rgba(255, 255, 255, 0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '32px',
                                fontWeight: 'bold',
                                backdropFilter: 'blur(10px)'
                            }}>
                                M
                            </div>
                            <Text style={{ 
                                fontSize: '14px', 
                                textAlign: 'center',
                                color: 'rgba(255, 255, 255, 0.9)',
                                fontWeight: 500
                            }}>
                                Maveshibids
                            </Text>
                        </div>
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 100%)',
                            opacity: 0,
                            transition: 'opacity 0.4s ease'
                        }} className="hover-overlay" />
                        
                        {/* Featured badge at bottom left of logo */}
                        {auction.is_featured && (
                            <div style={{
                                position: 'absolute',
                                bottom: 12,
                                left: 14,
                                zIndex: 3,
                                background: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)',
                                color: '#1f2937',
                                padding: '4px 12px',
                                borderRadius: '20px',
                                fontSize: '11px',
                                fontWeight: 700,
                                boxShadow: '0 2px 8px rgba(255, 215, 0, 0.4)',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                backdropFilter: 'blur(10px)'
                            }}>
                                ⭐ Featured
                            </div>
                        )}
                    </div>
                    <Button
                        type="text"
                        icon={auction.is_favorited ? <HeartFilled style={{ color: '#ff4d4f' }} /> : <HeartOutlined />}
                        onClick={(e) => toggleFavorite(auction.id, e)}
                        className={`favorite-button ${auction.is_favorited ? 'active' : ''}`}
                    />
                    {auction.is_favorited && (
                        <Tag color="red" className="favorite-tag">
                            <HeartFilled /> Favorite
                        </Tag>
                    )}
                    {/* <div style={{ 
                        position: 'absolute', 
                        top: 16, 
                        right: 16,
                        display: 'flex',
                        gap: '12px',
                        zIndex: 2
                    }}>
                        <Tag color={isLiveAuction ? "green" : getStatusColor(auction.status)} style={{ 
                            padding: '6px 16px',
                            borderRadius: '20px',
                            fontWeight: 600,
                            fontSize: '12px',
                            border: 'none',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}>
                            {isLiveAuction ? 'LIVE NOW' : auction.status.toUpperCase()}
                        </Tag>
                    </div> */}
                </div>
            }
            bodyStyle={{ padding: '20px' }}
        >
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                {/* Header Section - Category & Title */}
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <Tag 
                            color="blue" 
                            style={{ 
                                borderRadius: '16px',
                                padding: '4px 12px',
                                fontWeight: 600,
                                fontSize: '11px',
                                border: 'none',
                                margin: 0,
                                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                color: 'white',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}
                        >
                            {auction.category?.name || 'Uncategorized'}
                        </Tag>
                       {/* {auction.is_featured && (
                            <Tag 
                                icon={<FireOutlined />}
                                color="gold"
                                style={{ 
                                    borderRadius: '16px',
                                    padding: '4px 10px',
                                    fontWeight: 700,
                                    fontSize: '10px',
                                    border: 'none',
                                    margin: 0
                                }}
                            >
                                FEATURED
                            </Tag>
                        )} */}
                    </div>
                    <Title 
                        level={4} 
                        style={{ 
                            margin: 0, 
                            fontSize: '16px',
                            color: '#1f2937',
                            fontWeight: 700,
                            lineHeight: 1.4,
                            minHeight: '44px',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        {auction.title ? auction.title.charAt(0).toUpperCase() + auction.title.slice(1) : ''}
                    </Title>
                </div>

                {/* Auction Info Grid - Compact */}
                <div style={{ 
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '10px',
                    marginTop: '8px'
                }}>
                    {/* Lots Count */}
                    <div style={{ 
                        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                        padding: '10px 12px',
                        borderRadius: '10px',
                        border: '1px solid #bae6fd'
                    }}>
                        <Space size={6}>
                            <AppstoreOutlined style={{ color: '#0369a1', fontSize: '16px' }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <Text style={{ 
                                    color: '#0c4a6e',
                                    fontSize: '10px',
                                    fontWeight: 600,
                                    display: 'block',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    lineHeight: 1.2
                                }}>
                                    Lots
                                </Text>
                                <Text style={{ 
                                    color: '#075985',
                                    fontSize: '18px',
                                    fontWeight: 700,
                                    display: 'block',
                                    lineHeight: 1.2
                                }}>
                                    {auction.lots_count || 0}
                                </Text>
                            </div>
                        </Space>
                    </div>

                    {/* Status/Time */}
                    <div style={{ 
                        background: getAuctionStatus(auction) === 'Scheduled' 
                            ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)'
                            : auction.status === 'active'
                            ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)'
                            : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                        padding: '10px 12px',
                        borderRadius: '10px',
                        border: getAuctionStatus(auction) === 'Scheduled' 
                            ? '1px solid #fcd34d'
                            : auction.status === 'active'
                            ? '1px solid #6ee7b7'
                            : '1px solid #d1d5db'
                    }}>
                        <Space size={6}>
                            <ClockCircleOutlined style={{ 
                                color: getAuctionStatus(auction) === 'Scheduled' 
                                    ? '#d97706'
                                    : auction.status === 'active'
                                    ? '#059669'
                                    : '#6b7280',
                                fontSize: '16px' 
                            }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <Text style={{ 
                                    color: getAuctionStatus(auction) === 'Scheduled' 
                                        ? '#92400e'
                                        : auction.status === 'active'
                                        ? '#065f46'
                                        : '#4b5563',
                                    fontSize: '10px',
                                    fontWeight: 600,
                                    display: 'block',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    lineHeight: 1.2,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {getAuctionStatus(auction) === 'Scheduled' ? 'Starts In' : 'Status'}
                                </Text>
                                <Text style={{ 
                                    color: getAuctionStatus(auction) === 'Scheduled' 
                                        ? '#b45309'
                                        : auction.status === 'active'
                                        ? '#047857'
                                        : '#374151',
                                    fontSize: '13px',
                                    fontWeight: 700,
                                    display: 'block',
                                    lineHeight: 1.2,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {getAuctionStatus(auction) === 'Scheduled' 
                                        ? getTimeLeft(auction).split(' ')[0] + ' ' + getTimeLeft(auction).split(' ')[1]
                                        : auction.status === 'active' ? 'LIVE' : auction.status?.toUpperCase() || 'ACTIVE'}
                                </Text>
                            </div>
                        </Space>
                    </div>
                </div>

                {/* Date & Location Row */}
                <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    padding: '8px 0',
                    borderTop: '1px solid #f3f4f6',
                    borderBottom: '1px solid #f3f4f6'
                }}>
                    <Tooltip title={formatDateTime(auction.start_time)}>
                        <Space size={6} style={{ flex: 1, minWidth: 0 }}>
                            <CalendarOutlined style={{ color: '#6b7280', fontSize: '13px' }} />
                            <Text style={{ 
                                color: '#6b7280',
                                fontSize: '11px',
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}>
                                {moment(auction.start_time).format('MMM D, YYYY')}
                            </Text>
                        </Space>
                    </Tooltip>
                    {auction.location && (
                        <Tooltip title={auction.location}>
                            <Space size={6} style={{ flex: 1, minWidth: 0, justifyContent: 'flex-end' }}>
                                <EnvironmentOutlined style={{ color: '#6b7280', fontSize: '13px' }} />
                                <Text style={{ 
                                    color: '#6b7280',
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    textAlign: 'right'
                                }}>
                                    {auction.location.length > 15 ? auction.location.substring(0, 15) + '...' : auction.location}
                                </Text>
                            </Space>
                        </Tooltip>
                    )}
                </div>
                {/* Action Button - Full Width */}
                <div style={{ marginTop: '4px' }}>
                    {customButtons ? (
                        customButtons
                    ) : (
                        <Button 
                            type="primary"
                            block
                            className={isAuctionLiveNow ? 'live-bid-button' : ''}
                            onClick={(e) => {
                                e.stopPropagation();

                                if (canEnterAuction) {
                                    const newWindow = window.open(
                                        `/live-auctions/${auction.slug}`,
                                        '_blank',
                                        'toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=1920,height=1080'
                                    );
                                    if (newWindow) {
                                        newWindow.focus();
                                    }
                                } else {
                                    // Completed or cancelled - show auction details instead
                                    navigate(`/auctions/${auction.slug}`);
                                }
                            }}
                            style={{
                                borderRadius: '10px',
                                fontWeight: 600,
                                height: '38px',
                                padding: '0 16px',
                                background: !canEnterAuction
                                    ? 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)' // blue for "View Auction"
                                    : isAuctionLiveNow
                                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' // green for genuinely live
                                        : 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)', // blue while still scheduled
                                border: 'none',
                                boxShadow: canEnterAuction && isAuctionLiveNow
                                    ? '0 2px 8px rgba(16, 185, 129, 0.25)'
                                    : '0 2px 8px rgba(24, 144, 255, 0.25)',
                                transition: 'all 0.3s ease',
                                fontSize: '13px'
                            }}
                        >
                            {canEnterAuction ? (
                                <><FireOutlined style={{ marginRight: 6 }} /> Enter Auction</>
                            ) : (
                                <><EyeOutlined style={{ marginRight: 6 }} /> View Auction</>
                            )}
                        </Button>
                    )}
                </div>

                {/* Company Information Section - Compact */}
                <div 
                    style={{
                        padding: '10px 12px',
                        background: auction.company 
                            ? 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)'
                            : 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        cursor: auction.company ? 'pointer' : 'default',
                        transition: 'all 0.2s ease',
                        marginTop: '8px'
                    }}
                    onClick={(e) => {
                        if (auction.company) {
                            e.stopPropagation();
                            setCompanyDrawerVisible(true);
                        }
                    }}
                    onMouseEnter={(e) => {
                        if (auction.company) {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)';
                            e.currentTarget.style.borderColor = '#93c5fd';
                        }
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                    }}
                >
                    <Space size={10} style={{ width: '100%' }}>
                        {auction.company ? (
                            <>
                                {auction.company.company_logo_url ? (
                                    <Avatar
                                        src={auction.company.company_logo_url.startsWith('http') 
                                            ? auction.company.company_logo_url 
                                            : env.BACKEND_URL + auction.company.company_logo_url}
                                        alt={auction.company.company_name}
                                        size={32}
                                        style={{
                                            border: '2px solid #d1d5db',
                                            flexShrink: 0
                                        }}
                                    />
                                ) : (
                                    <Avatar
                                        size={32}
                                        style={{
                                            background: 'linear-gradient(135deg, #1890ff, #52c41a)',
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: '14px',
                                            border: '2px solid #d1d5db',
                                            flexShrink: 0
                                        }}
                                    >
                                        {auction.company.company_name?.charAt(0).toUpperCase() || 'C'}
                                    </Avatar>
                                )}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <Text strong style={{ 
                                        fontSize: '12px',
                                        color: '#1f2937',
                                        display: 'block',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        lineHeight: 1.3
                                    }}>
                                        {auction.company.company_name || 'Company'}
                                    </Text>
                                    {auction.company.short_tagline && (
                                        <Text type="secondary" style={{ 
                                            fontSize: '10px',
                                            display: 'block',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            lineHeight: 1.2,
                                            marginTop: '2px'
                                        }}>
                                            {auction.company.short_tagline}
                                        </Text>
                                    )}
                                </div>
                                <ShopOutlined style={{ 
                                    color: '#9ca3af',
                                    fontSize: '14px',
                                    flexShrink: 0
                                }} />
                            </>
                        ) : (
                            <>
                                <Avatar
                                    size={32}
                                    icon={<UserOutlined />}
                                    style={{
                                        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                                        color: 'white',
                                        border: '2px solid #d1d5db',
                                        flexShrink: 0
                                    }}
                                />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <Text strong style={{ 
                                        fontSize: '12px',
                                        color: '#1f2937',
                                        display: 'block',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        lineHeight: 1.3
                                    }}>
                                        Individual
                                    </Text>
                                </div>
                                <UserOutlined style={{ 
                                    color: '#9ca3af',
                                    fontSize: '14px',
                                    flexShrink: 0
                                }} />
                            </>
                        )}
                    </Space>
                </div>
            </Space>
        </Card>

        {/* Company Drawer */}
        {auction.company && (
            <CompanyDrawer
                visible={companyDrawerVisible}
                onClose={() => setCompanyDrawerVisible(false)}
                companyData={auction.company}
                userId={auction.user_id}
            />
        )}
        
        <style jsx>{`
            /* Live Bid Button Animation */
            .live-bid-button {
                animation: live-pulse 2s ease-in-out infinite;
                position: relative;
                overflow: hidden;
            }
            
            .live-bid-button::before {
                content: '';
                position: absolute;
                top: 0;
                left: -100%;
                width: 100%;
                height: 100%;
                background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
                animation: shimmer 3s ease-in-out infinite;
            }
            
            .live-bid-button:hover {
                animation: live-pulse-fast 1s ease-in-out infinite;
                transform: translateY(-2px);
                box-shadow: 0 8px 25px rgba(16, 185, 129, 0.6) !important;
                background: linear-gradient(135deg, #34d399 0%, #10b981 100%) !important;
            }
            
            .live-bid-button:hover::before {
                animation: shimmer-fast 1.5s ease-in-out infinite;
            }
            
            @keyframes live-pulse {
                0%, 100% {
                    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3), 0 0 0 0 rgba(16, 185, 129, 0.3);
                    transform: scale(1);
                }
                50% {
                    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3), 0 0 0 8px rgba(16, 185, 129, 0.1);
                    transform: scale(1.02);
                }
            }
            
            @keyframes live-pulse-fast {
                0%, 100% {
                    box-shadow: 0 8px 25px rgba(16, 185, 129, 0.6), 0 0 0 0 rgba(16, 185, 129, 0.6);
                    transform: scale(1.02);
                }
                50% {
                    box-shadow: 0 8px 25px rgba(16, 185, 129, 0.6), 0 0 0 12px rgba(16, 185, 129, 0.2);
                    transform: scale(1.05);
                }
            }
            
            @keyframes shimmer {
                0% {
                    left: -100%;
                }
                50% {
                    left: 100%;
                }
                100% {
                    left: 100%;
                }
            }
            
            @keyframes shimmer-fast {
                0% {
                    left: -100%;
                }
                100% {
                    left: 100%;
                }
            }
        `}</style>
        </div>
    );
};

export default AuctionCard; 